import type { OperationProgress, OperationRun } from '@foodlista/trigger-vue'
import {
  FAILED_RUN_STATUSES,
  PROGRESSING_RUN_STATUSES,
  RESUMABLE_RUN_STATUSES,
  TERMINAL_RUN_STATUSES,
} from '@foodlista/trigger-vue'

export type { OperationProgress }

export const PENDING_GRACE_MS = 60_000

export interface OperationEnvelopeView {
  createdAt: Date
  dismissedAt: Date | null
}

export type OperationStatus =
  | 'starting'
  | 'running'
  | 'completed'
  | 'failed'
  | 'canceled'

export function isOperationActive(
  envelope: OperationEnvelopeView,
  run: OperationRun | null,
  now: Date,
): boolean {
  if (envelope.dismissedAt) {
    return false
  }

  if (run) {
    return !TERMINAL_RUN_STATUSES.has(run.status)
  }

  return now.getTime() - envelope.createdAt.getTime() < PENDING_GRACE_MS
}

/**
 * Whether the operation has a run that is actively progressing right now.
 *
 * Narrower than `isOperationActive`, which also covers the pre-run grace
 * window and treats every non-terminal status as active. Use this one to gate
 * anything a stalled run must not hold hostage.
 */
export function hasProgressingRun(
  item: OperationEnvelopeView & { run: OperationRun | null },
): boolean {
  if (item.dismissedAt || !item.run) {
    return false
  }

  return PROGRESSING_RUN_STATUSES.has(item.run.status)
}

/**
 * Whether the run is parked in a status the platform resumes on its own
 * (`WAITING`, `DELAYED`, `PENDING_VERSION`, a retry). Such a run consumes no
 * compute right now, but it still executes later, so it must never be
 * presented — or treated — as finished.
 */
export function hasResumableRun(
  item: OperationEnvelopeView & { run: OperationRun | null },
): boolean {
  if (item.dismissedAt || !item.run) {
    return false
  }

  return RESUMABLE_RUN_STATUSES.has(item.run.status)
}

/**
 * Whether the operation is doing work *right now*: a progressing run, or an
 * envelope still waiting for its first run inside the pending grace window.
 *
 * Narrow on purpose — it gates the progress bar and the spinner, so a parked
 * run never animates forever. It is *not* the retention predicate: use
 * `isOperationUnfinished` for anything that decides whether an operation may
 * be cleared or stops being tracked.
 */
export function isOperationWorking(
  item: OperationEnvelopeView & { run: OperationRun | null },
  now: Date,
): boolean {
  return (
    hasProgressingRun(item)
    || (!item.run && isOperationActive(item, item.run, now))
  )
}

/**
 * Whether the platform still owes this operation an outcome: it is working, or
 * it is parked in a resumable status.
 *
 * This is the predicate the panel partitions and the dismiss control key off,
 * so a run that will resume cannot be hidden — dismissal would drop the feed
 * item on reload and with it the client-side completion handling. Runs that
 * were never observed, and statuses this app does not recognise, are *not*
 * unfinished: they belong to the recent list, where they can be cleared.
 */
export function isOperationUnfinished(
  item: OperationEnvelopeView & { run: OperationRun | null },
  now: Date,
): boolean {
  return isOperationWorking(item, now) || hasResumableRun(item)
}

export function operationStatus(
  run: OperationRun | null,
): OperationStatus {
  if (!run) {
    return 'starting'
  }

  if (run.status === 'COMPLETED') {
    return 'completed'
  }

  if (FAILED_RUN_STATUSES.has(run.status)) {
    return 'failed'
  }

  if (run.status === 'CANCELED') {
    return 'canceled'
  }

  return 'running'
}

export function operationProgress(
  run: OperationRun | null | undefined,
): OperationProgress | undefined {
  return run?.progress
}
